import { lazy, Suspense, useEffect, useState } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ApiError } from "@/api/client";
import { Spinner } from "@/components/Spinner";
import { BrowserRouter, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { useMe, useMySubscription } from "@/api/hooks";
import { isActiveSubscription } from "@/api/types";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { tg } from "@/lib/telegram";
import { openRetentionOffer } from "@/lib/retentionOffer";
import {
  closePromoGift,
  getPromoGift,
  openPromoGift,
  subscribePromoGift,
} from "@/lib/promoGift";
import { PromoGiftModal } from "@/components/PromoGiftModal";
import { Layout } from "@/components/Layout";
import { MaintenanceBanner } from "@/components/MaintenanceBanner";
import { BannedScreen } from "@/components/BannedScreen";
import { OnboardingPage } from "@/pages/Onboarding";
import { PromoPage } from "@/pages/Promo";
import { OffertaPage } from "@/pages/Offerta";
import { SavedPage } from "@/pages/Saved";
import { UnlockPage } from "@/pages/Unlock";
import { FeedPage } from "@/pages/Feed";
import { FeedVacancyPage } from "@/pages/FeedVacancy";
import { HomePage } from "@/pages/Home";
import { SubscriptionPage } from "@/pages/Subscription";
import { CategoriesPage } from "@/pages/Categories";
import { ProfilePage } from "@/pages/Profile";
import { ReferralsPage } from "@/pages/Referrals";
import { QuestsPage } from "@/pages/Quests";
import { CoursePage } from "@/pages/Course";
import { CourseLessonPage } from "@/pages/CourseLesson";
import { KnowledgeBasePage } from "@/pages/KnowledgeBase";
import { BirthdayConceptsPage } from "@/pages/BirthdayConcepts";
import { BirthdayGiftModal } from "@/components/BirthdayGiftModal";
import {
  closeBirthdayGift,
  isBirthdayGiftOpen,
  openBirthdayGift,
  subscribeBirthdayGift,
} from "@/lib/birthdayGift";
import { BirthdayGiftPage } from "@/pages/BirthdayGift";
import { LessonCardPage } from "@/pages/LessonCard";
import { PodcastsPage } from "@/pages/Podcasts";
import { WheelPage } from "@/pages/Wheel";
import { McpConnectPage } from "@/pages/McpConnect";
import { FeedbackPage } from "@/pages/Feedback";
import { InstaPromoPage } from "@/pages/InstaPromo";
import { KBArticlePage } from "@/pages/KBArticle";
import { KBDbArticlePage } from "@/pages/KBDbArticle";
// Адмінка вантажиться ОКРЕМИМ шматком (owner 2026-09-05: «вынеси админку из
// основного бандла»). Її бачать одиниці, а важить вона помітну частину
// застосунку — тепер решта людей її просто не качає.
const AdminPage = lazy(() =>
  import("@/pages/Admin").then((m) => ({ default: m.AdminPage })),
);
const AdminUserProfilePage = lazy(() =>
  import("@/pages/AdminUserProfile").then((m) => ({
    default: m.AdminUserProfilePage,
  })),
);
const PopupGalleryPage = lazy(() =>
  import("@/pages/PopupGallery").then((m) => ({ default: m.PopupGalleryPage })),
);
const IconGalleryPage = lazy(() =>
  import("@/pages/IconGallery").then((m) => ({ default: m.IconGalleryPage })),
);
import { AppAlert } from "@/components/AppAlert";
import { AppToast } from "@/components/AppToast";
import { WheelPurchaseWatcher } from "@/components/WheelPurchaseWatcher";
import { SubscriptionActivationWatcher } from "@/components/SubscriptionActivationWatcher";
import { FreezeCreditedWatcher } from "@/components/FreezeCreditedWatcher";
import { RetentionOfferModals } from "@/components/RetentionOffer";
import { QuestCelebrationWatcher } from "@/components/QuestCelebrationWatcher";
import { InstructionOverlay } from "@/components/InstructionOverlay";
import { SectionHint } from "@/components/SectionHint";
import { OptInPopup } from "@/components/OptInPopup";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Мережеві/таймаут-помилки (ApiError status 0) ретраїмо 3 рази з
      // backoff — щоб на поганому мобільному інеті апка сама відновилась,
      // а не показувала помилку/спінер (owner 2026-07-08, @kutnaksu на LTE).
      // 401/403 НЕ ретраїмо (initData не оновиться повтором).
      retry: (count, err) => {
        if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
          return false;
        }
        return count < 3;
      },
      retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 8000),
      refetchOnWindowFocus: true,
    },
  },
});

/**
 * StartParamRouter — парсить Telegram WebApp.start_param при mount і
 * виконує авто-навігацію на потрібний deeplink-маршрут.
 *
 * Підтримувані схеми:
 *   vac_<id>        → /feed/:id    (відкриття конкретної вакансії з бота)
 *   feed            → /feed        (загальний feed)
 *
 * Запускається ОДИН раз при першому mount BrowserRouter. Подальша
 * навігація — normal React Router.
 */
/**
 * OnboardingRedirectGuard — глобальний охоронець: якщо юзер не пройшов
 * онбординг повністю (subscribed_main_channel AND has_categories) і він
 * НЕ admin — всі роути окрім /onboarding редиректять на /onboarding.
 *
 * Юзер 2026-05-26: «поки людина не пройшла онбординг — Mini App має
 * показувати ТІЛЬКИ цей екран. Кнопка «Налаштувати бот» і відкриття
 * Mini App ведуть до одного flow».
 */
function OnboardingRedirectGuard() {
  const navigate = useNavigate();
  const location = useLocation();
  const me = useMe();
  useEffect(() => {
    if (!me.data) return;
    if (me.data.is_admin) return;
    // Крок 1 — сфери. Поки їх немає, людині нема чого показувати.
    if (!me.data.onboarding_completed) {
      if (location.pathname !== "/onboarding") {
        navigate("/onboarding", { replace: true });
      }
      return;
    }
    // Крок 2 — підписка на канал (owner 2026-09-09). Сфери обрані, але доступ
    // до підбірки ще закритий: ведемо на екран замків, а не в порожню стрічку.
    // Сам онбординг і екран замків лишаємо доступними, інакше вийде петля.
    if (!me.data.selection_unlocked) {
      if (
        location.pathname !== "/unlock" &&
        location.pathname !== "/onboarding"
      ) {
        navigate("/unlock", { replace: true });
      }
    }
  }, [me.data, location.pathname, navigate]);
  return null;
}

/**
 * RouteSectionHint — за поточним маршрутом показує контекстну підказку розділу
 * (1 раз при першому вході). Централізовано, щоб не чіпати кожну сторінку.
 */
function RouteSectionHint() {
  const { pathname } = useLocation();
  let hint: string | null = null;
  if (pathname.startsWith("/feed")) hint = "feed";
  else if (pathname.startsWith("/profile")) hint = "profile";
  else if (pathname.startsWith("/knowledge-base")) hint = "kb";
  else if (pathname.startsWith("/quests")) hint = "quests";
  else if (pathname.startsWith("/referrals")) hint = "referrals";
  else if (pathname.startsWith("/course")) hint = "course";
  if (!hint) return null;
  return <SectionHint hint={hint} />;
}

/**
 * FreeOptInWatcher — попап-опитування free-тарифу (Owner 2026-07-09, Крок 2).
 *
 * ТЕСТ-ФАЗА: показуємо ЛИШЕ адмінам (щоб власник сам протестив вигляд/флоу),
 * АБО якщо URL містить ?optin=1 (форс для повторного тесту). ПІЗНІШЕ, коли
 * власник підтвердить — умова Варіанту 2 для ВСІХ free:
 *   !is_admin && !has_active_subscription && !free_optin && has_categories
 * (+ показ по кнопці рассилки). Доставку free вмикаємо окремим кроком.
 */
function FreeOptInWatcher() {
  const me = useMe();
  const subQuery = useMySubscription();
  const [dismissed, setDismissed] = useState(false);

  // Форс для тесту/прев'ю: ?optin=1 показує завжди.
  const forced =
    typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).get("optin") !== null;

  // Варіант 2 (owner 2026-07-10): показуємо РЕАЛЬНИМ free-юзерам, які:
  //  • не адмін;
  //  • ще НЕ отримують вакансії — тобто або не дали згоду (free_optin=false),
  //    АБО на паузі (free_paused=true) → попап як re-активація;
  //  • пройшли онбординг (є сфери + підписані на канал) — щоб не спливати
  //    поверх /onboarding;
  //  • не мають активної підписки.
  const md = me.data;
  const sub = subQuery.data;
  const isFreeEligible =
    !!md &&
    !md.is_admin &&
    (!md.free_optin || md.free_paused) &&
    md.onboarding_completed &&
    !!sub &&
    !isActiveSubscription(sub);

  // Прийшов по промо-посиланню (owner 2026-07-25): на сторінці промокоду
  // попап про безкоштовний доступ НЕ показуємо — він перекривав офер і збивав
  // людину з оплати. Пішов далі по мініапці — попап працює як завжди.
  const onPromoPage = useLocation().pathname.startsWith("/promo");

  const shouldShow = (forced || isFreeEligible) && !dismissed && !onPromoPage;
  return (
    <OptInPopup open={shouldShow} onClose={() => setDismissed(true)} />
  );
}

function StartParamRouter() {
  const navigate = useNavigate();
  useEffect(() => {
    // 1) Query string: ?vac=N (primary — Telegram передає завжди)
    const search = new URLSearchParams(window.location.search);
    const vacQ = search.get("vac");
    if (vacQ && /^\d+$/.test(vacQ)) {
      // `ai=style` тягнемо далі: кнопка в боті «Створити свій стиль 💫»
      // має одразу відкрити форму стилю на картці (owner 2026-08-31).
      const extra = search.get("ai") === "style" ? "?ai=style" : "";
      window.history.replaceState(null, "", "/");
      navigate(`/feed/${vacQ}${extra}`, { replace: true });
      return;
    }
    if (search.get("feed") !== null) {
      window.history.replaceState(null, "", "/");
      navigate("/feed", { replace: true });
      return;
    }
    // Кнопка «Відкрити квести» з анонсу нових квестів (owner 2026-09-16:
    // «кнопка відкрити квести кидає на головну» — параметра тут не було).
    if (search.get("quests") !== null) {
      window.history.replaceState(null, "", "/");
      navigate("/quests", { replace: true });
      return;
    }
    // Кнопка «Перейти до бонусів» з бот-повідомлення про згорання бонусів.
    if (search.get("bonus") !== null) {
      window.history.replaceState(null, "", "/");
      navigate("/quests", { replace: true });
      return;
    }
    // Кнопка з нагадування про спецпропозицію (owner 2026-07-30): відкриваємо
    // САМ попап офера, а не просто сторінку підписки — інакше людина не
    // розуміє, що саме їй показали.
    if (search.get("offer") !== null) {
      window.history.replaceState(null, "", "/");
      navigate("/subscription", { replace: true });
      openRetentionOffer("offer");
      return;
    }
    // Кнопка «Змінити сфери» під FREE-вакансією в боті (owner 2026-07-11) —
    // веде на мультиселект сфер, щоб швидко оновити підбірку.
    // Промокод, надісланий у бот текстом (owner 2026-08-28): відкриваємо
    // ГОЛОВНУ, а поверх неї — попап подарунка. Окремий екран промо для цього
    // випадку owner забракував.
    const gift = search.get("gift");
    // ⚠️ `/subscription?gift=1` — це кнопка оффера маркетинг-машини, її
    // розбирає сама сторінка підписки. Промокод приходить як `/?gift=<slug>`.
    // Без цієї перевірки промо-гілка (2026-08-28) перехоплювала ВСІ офери:
    // людину кидало на головну і попап не відкривався взагалі (owner
    // 2026-09-01: «нажимаю забрать 45 дней — ничего не происходит»).
    const isOfferLink =
      gift === "1" || window.location.pathname.startsWith("/subscription");
    if (gift && !isOfferLink) {
      window.history.replaceState(null, "", "/");
      navigate("/", { replace: true });
      openPromoGift(gift);
      return;
    }
    if (search.get("spheres") !== null) {
      window.history.replaceState(null, "", "/");
      navigate("/categories", { replace: true });
      return;
    }

    // 2) Hash-based deeplink (#vac_123) — fallback якщо query видалено
    const hash = window.location.hash.replace(/^#/, "");
    if (hash) {
      const vacMatch = /^vac_(\d+)$/.exec(hash);
      if (vacMatch) {
        window.history.replaceState(null, "", "/");
        navigate(`/feed/${vacMatch[1]}`, { replace: true });
        return;
      }
      const lessonHash = /^lesson_(\d+)$/.exec(hash);
      if (lessonHash) {
        window.history.replaceState(null, "", "/");
        navigate(`/knowledge-base?lesson=${lessonHash[1]}`, { replace: true });
        return;
      }
      if (hash === "feed") {
        window.history.replaceState(null, "", "/");
        navigate("/feed", { replace: true });
        return;
      }
    }

    // 2.5) ?lesson=<id> у URL мініаппи. Саме query, а НЕ hash: Telegram
    // дописує до адреси свій `#tgWebAppData=…` і затирає наш фрагмент —
    // через це кнопка «Дивитись урок» відкривала головну (owner 2026-09-12).
    const qs = new URLSearchParams(window.location.search);
    if (qs.get("birthday")) {
      // Подарунок — ПОПАП, а не сторінка (owner 2026-09-13). Диплінк лишає
      // людину там, де вона є, і просто показує вибір.
      window.history.replaceState(null, "", "/");
      openBirthdayGift();
      return;
    }
    const qsLesson = qs.get("lesson");
    if (qsLesson && /^\d+$/.test(qsLesson)) {
      navigate(`/knowledge-base?lesson=${qsLesson}`, { replace: true });
      return;
    }

    // 3) Telegram WebApp startapp-deeplink (t.me/<bot>?startapp=...)
    const param = tg()?.initDataUnsafe?.start_param;
    if (!param) return;
    const vacMatch = /^vac_(\d+)$/.exec(param);
    if (vacMatch) {
      navigate(`/feed/${vacMatch[1]}`, { replace: true });
      return;
    }
    // Урок бази знань (owner 2026-09-12): лист про новий урок має вести
    // одразу в сам урок, а не в список.
    const lessonMatch = /^lesson_(\d+)$/.exec(param);
    if (lessonMatch) {
      navigate(`/knowledge-base?lesson=${lessonMatch[1]}`, { replace: true });
      return;
    }
    if (param === "feed") {
      navigate("/feed", { replace: true });
      return;
    }
    // t.me/inflow_chatbot?startapp=insta — посилання для Instagram (owner 2026-10-14).
    if (param === "insta") {
      navigate("/insta", { replace: true });
      return;
    }
    // Посилання з відповідей ШІ через MCP (owner 2026-10-30): «Оформити
    // підписку», «Збережені» і сторінка підключення.
    if (param === "sub") {
      navigate("/subscription", { replace: true });
      return;
    }
    if (param === "saved") {
      navigate("/saved", { replace: true });
      return;
    }
    if (param === "mcp") {
      navigate("/mcp", { replace: true });
    }
  }, [navigate]);
  return null;
}

/** Подарунок на день народження — попап, змонтований глобально. */
function BirthdayGiftPortal() {
  const [open, setOpen] = useState(() => isBirthdayGiftOpen());
  useEffect(() => {
    setOpen(isBirthdayGiftOpen());
    return subscribeBirthdayGift(setOpen);
  }, []);
  if (!open) return null;
  return <BirthdayGiftModal onClose={closeBirthdayGift} />;
}

/** Попап подарунка за промокодом — змонтований глобально, як ретеншн-офер. */
function PromoGiftPortal() {
  // Початкове значення читаємо з сигналу, а не з null: StartParamRouter
  // змонтований ВИЩЕ і встигає викликати openPromoGift ще до того, як цей
  // компонент підпишеться. Через це попап не зʼявлявся (owner 2026-08-28).
  const [slug, setSlug] = useState<string | null>(() => getPromoGift());
  useEffect(() => {
    // Ефект StartParamRouter спрацьовує РАНІШЕ (він вище в дереві), тому
    // сигнал уже міг пролетіти: спершу перечитуємо поточне значення, і лише
    // потім підписуємось (owner 2026-08-28 — попап не відкривався).
    setSlug(getPromoGift());
    return subscribePromoGift(setSlug);
  }, []);
  if (!slug) return null;
  return <PromoGiftModal slug={slug} onClose={closePromoGift} />;
}

export function App() {
  return (
    <ErrorBoundary>
      <MaintenanceBanner />
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <StartParamRouter />
          <OnboardingRedirectGuard />
          {/* Глобальний in-app алерт (замість системного tg.showAlert) */}
          <AppAlert />
          {/* Маленький тост знизу (копіювання реф-посилання тощо) */}
          <AppToast />
          {/* Фуллскрін-заглушка для адмін-забанених (юзер 2026-06-23) */}
          <BannedScreen />
          {/* Глобальний детект активації підписки → celebration-попап на
              будь-якому екрані (юзер 2026-06-15) */}
          <SubscriptionActivationWatcher />
          <WheelPurchaseWatcher />
          <FreezeCreditedWatcher />
          {/* Глобальні модалки ретеншн-офера (попап-2 офер + попап-3
              нагадування), відкриваються зі скасування/закрепу (юзер 2026-07-04) */}
          <RetentionOfferModals />
          {/* Подарунок за промокодом, надісланим у бот (owner 2026-08-28) */}
          <PromoGiftPortal />
          <BirthdayGiftPortal />
          {/* Celebration-попап виконаного квесту (юзер 2026-06-25) */}
          <QuestCelebrationWatcher />
          {/* Базова інструкція (після підписки) + контекстні підказки розділів
              при першому вході (юзер 2026-06-15) */}
          <InstructionOverlay />
          <RouteSectionHint />
          {/* Попап-опитування free-тарифу — тест-фаза: лише адмінам (owner 2026-07-09) */}
          <FreeOptInWatcher />
          <Routes>
            {/* Onboarding — окремий standalone-flow, БЕЗ Layout/nav.
                Сюди потрапляють нові юзери з бот-кнопки «Налаштувати бот». */}
            <Route path="/onboarding" element={<OnboardingPage />} />
            <Route path="/promo/:slug" element={<PromoPage />} />
            <Route element={<Layout />}>
              <Route path="/" element={<HomePage />} />
              <Route path="/subscription" element={<SubscriptionPage />} />
              <Route path="/categories" element={<CategoriesPage />} />
              <Route path="/profile" element={<ProfilePage />} />
              <Route path="/referrals" element={<ReferralsPage />} />
              <Route path="/quests" element={<QuestsPage />} />
              <Route path="/course" element={<CoursePage />} />
              <Route path="/course/:idx" element={<CourseLessonPage />} />
              <Route path="/knowledge-base" element={<KnowledgeBasePage />} />
              {/* Обкладинка анонсу уроку — з неї робимо скріншот (owner
                  2026-09-12: картинка мусить бути один в один застосунок). */}
              <Route path="/card/lesson" element={<LessonCardPage />} />
              {/* Подарунок на день народження — вибір із трьох (owner
                  2026-09-13). Кнопка з привітання веде саме сюди. */}
              <Route path="/birthday" element={<BirthdayGiftPage />} />
              {/* Галерея макетів подарунка — тільки для показу власнику. */}
              <Route path="/birthday-concepts" element={<BirthdayConceptsPage />} />
              <Route path="/podcasts" element={<PodcastsPage />} />
              <Route path="/wheel" element={<WheelPage />} />
              <Route path="/mcp" element={<McpConnectPage />} />
              <Route path="/feedback" element={<FeedbackPage />} />
              {/* Тиждень за підписку на Instagram (owner 2026-10-14). */}
              <Route path="/insta" element={<InstaPromoPage />} />
              <Route path="/knowledge-base/article/:slug" element={<KBArticlePage />} />
              <Route path="/knowledge-base/db-article/:id" element={<KBDbArticlePage />} />
              <Route path="/offerta" element={<OffertaPage />} />
              <Route path="/feed" element={<FeedPage />} />
              <Route path="/saved" element={<SavedPage />} />
              <Route path="/unlock" element={<UnlockPage />} />
              <Route path="/feed/:id" element={<FeedVacancyPage />} />
              <Route
                path="/admin"
                element={
                  <Suspense fallback={<Spinner />}>
                    <AdminPage />
                  </Suspense>
                }
              />
              <Route
                path="/admin/popups"
                element={
                  <Suspense fallback={<Spinner />}>
                    <PopupGalleryPage />
                  </Suspense>
                }
              />
              <Route
                path="/admin/icons"
                element={
                  <Suspense fallback={<Spinner />}>
                    <IconGalleryPage />
                  </Suspense>
                }
              />
              <Route
                path="/admin/user/:userId"
                element={
                  <Suspense fallback={<Spinner />}>
                    <AdminUserProfilePage />
                  </Suspense>
                }
              />
              <Route path="*" element={<HomePage />} />
            </Route>
          </Routes>
        </BrowserRouter>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}
